import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

// Labels resolved at render from the `home` namespace (home:prayer.labels.*).
const ORDER = ["Fajr", "Sunrise", "Dhuhr", "Asr", "Maghrib", "Isha"];

function to12h(hhmm) {
  if (!hhmm) return "—";
  const [hS, mS] = hhmm.split(":");
  let h = parseInt(hS, 10);
  const m = mS.padStart(2, "0");
  const ampm = h >= 12 ? "pm" : "am";
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
}

export default function PrayerTime({
  prClass = "",
  // If you prefer not to ask for geolocation, set useGeo = false
  useGeo = true,
  city = "Rabat",
  country = "Morocco", // "MA" also works
  methodId = 21,        // 3 = Muslim World League (safe default)
  asrSchool = 0,       // 0=Shafi, 1=Hanafi
  timeFormat = "24h",  // "24h" | "12h"
  // You can force coordinates (overrides geolocation & city)
  latitude,
  longitude,
}) {
  const { t } = useTranslation("home");
  const [times, setTimes] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setErr("");

      try {
        let url;

        // 1) If explicit coords provided, use them
        if (typeof latitude === "number" && typeof longitude === "number") {
          url = `https://api.aladhan.com/v1/timings?latitude=${latitude}&longitude=${longitude}&method=${methodId}&school=${asrSchool}`;
        } else if (useGeo && navigator.geolocation) {
          // 2) Try geolocation
          try {
            const coords = await new Promise((resolve, reject) =>
              navigator.geolocation.getCurrentPosition(
                pos => resolve(pos.coords),
                reject,
                { enableHighAccuracy: true, timeout: 6000 }
              )
            );
            url = `https://api.aladhan.com/v1/timings?latitude=${coords.latitude}&longitude=${coords.longitude}&method=${methodId}&school=${asrSchool}`;
          } catch {
            // 3) Fallback: by city/country
            url = `https://api.aladhan.com/v1/timingsByCity?city=${encodeURIComponent(city)}&country=${encodeURIComponent(country)}&method=${methodId}&school=${asrSchool}`;
          }
        } else {
          // 4) No geo: by city/country
          url = `https://api.aladhan.com/v1/timingsByCity?city=${encodeURIComponent(city)}&country=${encodeURIComponent(country)}&method=${methodId}&school=${asrSchool}`;
        }

        const res = await fetch(url);
        const json = await res.json();
        if (!json || json.code !== 200) throw new Error("API error");
        if (cancelled) return;

        setTimes(json.data?.timings || {});
      } catch (e) {
        if (!cancelled) setErr("Couldn't load prayer times.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [useGeo, city, country, methodId, asrSchool, latitude, longitude]);

  const display = (k) => {
    const raw = times?.[k] || "";
    return timeFormat === "12h" ? to12h(raw) : (raw || "—");
  };

  return (
    <div className={`prayertime-section ${prClass}`}>
      <div className="container">
        <div className="prayertime-wrap">
          <div className="row g-0" id="prayerTimeRow">
            {loading && !times && !err && (
              <div className="col-12">
                <div className="item"><h2>{t("prayer.title")}</h2><span>{t("prayer.loading")}</span></div>
              </div>
            )}

            {err && (
              <div className="col-12">
                <div className="item"><h2>{t("prayer.title")}</h2><span>{t("prayer.error")}</span></div>
              </div>
            )}

            {!err && times && ORDER.map((key) => (
              <div key={key} className="col-lg-2 col-md-4 col-sm-6 col-12">
                <div className="item">
                  <h2>{t(`prayer.labels.${key}`)}</h2>
                  <span>{display(key)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
